import { useNavigate, useParams } from 'react-router-dom'
import { ID, PortFlashInfo } from '../types/models'
import { ConfigMessage, isConfigMessage, isUpdateMessage } from '../types/messaging'
import { Trans, useTranslation } from 'react-i18next'
import { TopTitle } from '../components/atoms/TopTitle'
import { useApp } from '../hooks/useApp'
import { Box, Button } from '@mui/material'
import { callAxios } from '../hooks/useAxios'
import { DeviceControl } from '../classes/DeviceControl'
import { PortConfigDiff } from '../types/ports'
import React, { useEffect, useState } from 'react'
import { useDevices } from '../hooks/useDevices'
import { useMessageFilter } from '../hooks/useMessageFilter'
import { CachedTwoTone, CheckCircle, ErrorOutline } from '@mui/icons-material'
import { TickList, TickListValue } from '../components/atoms/TickList'
import { isDebug } from '../utils/misc'
import { PageSubtitle } from '../components/atoms/PageSubtitle'
import { CONF_READ_TIMEOUT, REBOOT_TIMEOUT } from '../types/constants'
import { Framed } from '../components/atoms/Framed'

type FailureType = 'BUSY' | 'TIMEOUT' | 'FAILED' | 'OK' | 'TRIVIAL'

export function SaveConfigPage(): React.ReactElement {

  const params = useParams()
  const { id } = params
  const { t } = useTranslation()
  const [hardSaved, setHardSaved] = useState<TickListValue>(null)
  const [booted, setBooted] = useState<TickListValue>(null)
  const [verified, setVerified] = useState<TickListValue>(null)
  const [state, setState] = useState<FailureType>('BUSY')
  // Debug data
  const [waitingPorts, setWaitingPorts] = useState<Set<number>>(new Set())
  const [failures, setFailures] = useState<PortConfigDiff[]>([])
  const [hwConfigSent, setHwConfigSent] = useState<any>(null)

  const navigate = useNavigate()
  const app = useApp()
  const { control } = useDevices()

  const deviceControl = control.controls({ deviceId: parseInt(id || '0') as ID })[0]

  // Build explicit flash items for ports with pending config.
  const flash: PortFlashInfo[] = []
  const hardPortIDs: ID[] = []

  for (const portCtrl of deviceControl.getAllPortControls()) {
    if (portCtrl.uncommittedConfig() !== null) {
      const item = DeviceControl.buildPortFlashItem(portCtrl)
      hardPortIDs.push(item.id)
      flash.push({ port: item.port, config: item.config })
    }
  }

  // Calculate overall status based on the pieces.
  const updateOverallState = () => {
    if (state === 'BUSY') {
      if (booted === false) {
        setState('TIMEOUT')
        return
      }
      if (verified === false) {
        setState('FAILED')
      }
      if (hardSaved === undefined && booted === undefined && verified === undefined) {
        setState('TRIVIAL')
        return
      }
      if (hardSaved === true && booted === true && verified === true) {
        setState('OK')
      }
      if (hardSaved === undefined && booted === undefined && verified === undefined) {
        setState('OK')
      }
    }
  }
  updateOverallState()

  useMessageFilter({
    filter: s => isUpdateMessage(s) && ('boot' in s.values) && s.values.boot === true && s.hw_id === deviceControl.device.hw_id,
    onMatch: () => {
      setBooted(true)
    },
    timeout: REBOOT_TIMEOUT,
    onTimeout: () => {
      setBooted(false)
    }
  })

  useMessageFilter({
    filter: s => isConfigMessage(s) &&
      ('hardware' in s.config) &&
      (s.config as Record<string, object>).hardware &&
      Object.keys((s.config as Record<string, object>).hardware).length > 0,
    onMatch: (msg, state: Set<number>) => {
      const portId = (msg as unknown as ConfigMessage).port_id
      if (state.has(portId)) {
        setWaitingPorts(state)
        state.delete(portId)
        if (state.size === 0) {
          let isGood = true
          for (const hwPortId of hardPortIDs) {
            const ctrl = control.ports({ portId: hwPortId })
            if (ctrl.length === 0) {
              console.error(`Strangely cannot locate control for port ${hwPortId}?`)
              return
            }
            const diff = ctrl[0].hardwareDiff()
            if (diff.type === 'DIFFERS' || diff.type === 'READ FAILED') {
              setFailures(failures.concat(diff))
              isGood = false
            }
          }
          setVerified(isGood)
        }
      }
      return state
    },
    timeout: CONF_READ_TIMEOUT,
    onTimeout: () => {
      setBooted((booted) => booted === null ? false : booted)
      // Last minute check in case we missed.
      if (booted && waitingPorts.size === 0) {
        setVerified(true)
      } else {
        setVerified(false)
      }
    },
    isReady: (state: Set<number>) => state.size === 0,
    initialState: new Set(hardPortIDs)
  })

  useEffect(() => {

    setWaitingPorts(new Set(hardPortIDs))

    const handleSaving = async (): Promise<void> => {
      if (flash.length) {
        setHwConfigSent(flash)
        // Flash.
        await callAxios({
          method: 'PATCH',
          url: `/devices/config/${id}`,
        }, { flash }).then(res => {
          setHardSaved(res.success)
          if (!res.success) {
            app.addError(res)
          }
        })
      } else {
        setHardSaved(undefined)
        setBooted(undefined)
        setVerified(undefined)
      }
    }

    handleSaving()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onContinue = () => {
    control.clearPendingConfig()
    if (state === 'OK' || state === 'TRIVIAL') {
      navigate('/')
    } else {
      navigate(`/config/${params.id}/${params.portId || ''}`)
    }
  }

  return (
    <>
      <TopTitle title={t('Device Configuration')}></TopTitle>
      <Framed>
        <>
        {state === 'BUSY' && <Trans>Saving Configuration...</Trans>}
        {state === 'TIMEOUT' && <Trans>Timed Out</Trans>}
        {state === 'OK' && <Trans>Completed!</Trans>}
        {state === 'TRIVIAL' && <Trans>Nothing to do</Trans>}

        <Box sx={{ mt: 3, mb: 2, fontSize: 61 }}>
          {state === 'BUSY' && <CachedTwoTone sx={{ width: '8rem', height: '8rem' }} className="rev-rotating-slow"/>}
          {(state === 'TIMEOUT' || state === 'FAILED') && <ErrorOutline sx={{ width: '8rem', height: '8rem', color: 'error.main' }}/>}
          {(state === 'OK' || state === 'TRIVIAL') && <CheckCircle sx={{ width: '8rem', height: '8rem', color: 'success.main' }}/>}
        </Box>

        <TickList
          names={[t('Hardware configuration sent'), t('Device rebooted'), t('Configuration verified')]}
          states={[hardSaved, booted, verified]}
        />

        <Box sx={{ mt: 3, fontSize: 15 }}>
          {state === 'TIMEOUT' && <Trans>We were not able to connect to the device. Please retry later.</Trans>}
          {state === 'FAILED' && <Trans>The resulting configuration differs from the values we wanted. Please check.</Trans>}
        </Box>

        <Button
          sx={{ mt: 3 }}
          disabled={state === 'BUSY'}
          data-cy="button-Continue"
          onClick={onContinue}
        >
          <Trans>Continue</Trans>
        </Button>

        {isDebug('flashing') && <Box sx={{ textAlign: 'left', fontSize: 16 }}>
            <PageSubtitle title={t('Hardware Configuration Sent')}/>
            <pre>{JSON.stringify(hwConfigSent, null, 2)}</pre>
            <PageSubtitle title={t('Verification failures')}/>
            <pre>{JSON.stringify(failures, null, 2)}</pre>
            <PageSubtitle title={t('Waiting for configuration')}/>
            <pre>{JSON.stringify([...waitingPorts], null, 2)}</pre>
          </Box>
        }
        </>
      </Framed>
    </>
  )
}
